import React, { useState } from "react";
import api from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ScanLine, FileText, Plus, Trash2, Hash, Car } from "lucide-react";
import EntityPicker from "@/components/EntityPicker";

const TECHS_AND_LOCATIONS_HINT = "Optional — assigned after the calibration is scheduled.";

export default function NewJob() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    vin: "", customer_name: "", insurance_company: "", claim_number: "", policy_number: "",
    body_shop_name: "", vehicle_year: "", vehicle_make: "", vehicle_model: "", notes: "",
    customer_id: "", vehicle_id: "", body_shop_id: "", insurance_id: "",
    technician_id: "", location_id: "",
    adjuster_name: "", adjuster_email: "",
  });
  const [customerVehicles, setCustomerVehicles] = useState([]);
  const [pickedTech, setPickedTech] = useState("");
  const [pickedLocation, setPickedLocation] = useState("");
  const [lines, setLines] = useState([]);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [vinLoading, setVinLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // ───── VIN decode (real NHTSA) ─────
  const decodeVin = async () => {
    const vin = (form.vin || "").trim().toUpperCase();
    if (vin.length < 11) return toast.error("Enter a VIN (11-17 chars) first");
    setVinLoading(true);
    try {
      const r = await api.get(`/vin/decode/${vin}`);
      const d = r.data || {};
      setForm((f) => ({
        ...f,
        vin,
        vehicle_year: d.year ?? f.vehicle_year,
        vehicle_make: d.make || f.vehicle_make,
        vehicle_model: d.model || f.vehicle_model,
      }));
      toast.success(`Decoded · ${d.year || ""} ${d.make || ""} ${d.model || ""}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "VIN decode failed");
    } finally {
      setVinLoading(false);
    }
  };

  // ───── OCR fallback (kept for legacy demo flow) ─────
  const runOCR = async () => {
    setOcrLoading(true);
    try {
      const res = await api.post("/jobs/ocr/parse", { file_name: "sample-estimate.pdf" });
      const r = res.data;
      setForm((f) => ({
        ...f,
        vin: r.vin || f.vin,
        customer_name: r.customer_name || f.customer_name,
        insurance_company: r.insurance_company || f.insurance_company,
        claim_number: r.claim_number || f.claim_number,
      }));
      setLines(r.lines || []);
      toast.success(`OCR parsed · ${r.lines?.length || 0} lines extracted`);
    } catch {
      toast.error("OCR failed");
    } finally {
      setOcrLoading(false);
    }
  };

  // ───── Pickers ─────
  const pickCustomer = (c) => {
    const full = `${c.first_name || ""} ${c.last_name || ""}`.trim();
    setForm((f) => ({
      ...f,
      customer_id: c.id,
      customer_name: full || c.email || "Unknown",
      insurance_company: c.insurance_company || f.insurance_company,
      policy_number: c.policy_number || f.policy_number,
      vehicle_id: "", vin: "", vehicle_make: "", vehicle_model: "", vehicle_year: "",
    }));
    setCustomerVehicles(c.vehicles || []);
  };
  const clearCustomer = () => {
    setForm((f) => ({ ...f, customer_id: "", customer_name: "" }));
    setCustomerVehicles([]);
  };
  const pickVehicle = (v) => {
    setForm((f) => ({
      ...f,
      vehicle_id: v.vin || `${v.year}-${v.make}-${v.model}`,
      vin: v.vin || f.vin,
      vehicle_year: v.year || f.vehicle_year,
      vehicle_make: v.make || f.vehicle_make,
      vehicle_model: v.model || f.vehicle_model,
    }));
  };
  const pickBodyShop = (b) =>
    setForm((f) => ({ ...f, body_shop_id: b.id, body_shop_name: b.company_name || b.name }));
  const clearBodyShop = () => setForm((f) => ({ ...f, body_shop_id: "", body_shop_name: "" }));
  const pickInsurance = (i) =>
    setForm((f) => ({ ...f, insurance_id: i.id, insurance_company: i.name }));
  const clearInsurance = () => setForm((f) => ({ ...f, insurance_id: "", insurance_company: "" }));
  const pickTech = (t) => { setForm((f) => ({ ...f, technician_id: t.id })); setPickedTech(t.name); };
  const clearTech = () => { setForm((f) => ({ ...f, technician_id: "" })); setPickedTech(""); };
  const pickLocation = (l) => { setForm((f) => ({ ...f, location_id: l.id })); setPickedLocation(l.name); };
  const clearLocation = () => { setForm((f) => ({ ...f, location_id: "" })); setPickedLocation(""); };

  // ───── Estimate lines ─────
  const addLine = () =>
    setLines([...lines, { line_no: lines.length + 1, operation: "LBR", description: "", part_number: "", hours: 0, amount: 0 }]);
  const updateLine = (idx, key, val) => {
    const next = [...lines];
    next[idx][key] = key === "amount" || key === "hours" ? parseFloat(val) || 0 : val;
    setLines(next);
  };
  const removeLine = (idx) => setLines(lines.filter((_, i) => i !== idx));

  // ───── Submit ─────
  const submit = async () => {
    if (!form.vin || form.vin.length < 11) return toast.error("VIN required (min 11 chars)");
    if (!form.customer_name) return toast.error("Customer required — pick or type a name");
    setSaving(true);
    try {
      const payload = {
        ...form,
        vehicle_year: form.vehicle_year ? parseInt(form.vehicle_year) : null,
        estimate_lines: lines,
      };
      // Strip empty FK ids
      ["customer_id", "vehicle_id", "body_shop_id", "insurance_id", "technician_id", "location_id"]
        .forEach((k) => { if (!payload[k]) delete payload[k]; });
      const res = await api.post("/jobs", payload);
      toast.success(`Job created · status: ${res.data.status.replace(/_/g, " ")}`);
      navigate(`/jobs/${res.data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create job");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 animate-slide-up max-w-5xl" data-testid="new-job-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-bold mb-2">New Job · Intake</div>
          <h1 className="font-display text-4xl font-semibold tracking-tight">Create Calibration Job</h1>
          <p className="text-sm text-slate-400 mt-1.5">Pick or create the customer / vehicle / body shop / insurance — everything links to one operational record.</p>
        </div>
        <Button
          data-testid="run-ocr-btn"
          onClick={runOCR}
          disabled={ocrLoading}
          variant="outline"
          className="border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10"
        >
          <ScanLine className="w-4 h-4 mr-2" />
          {ocrLoading ? "Parsing…" : "Run Mock OCR (Sample)"}
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {/* ───── Vehicle ───── */}
        <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Vehicle</div>
            <Car className="w-4 h-4 text-slate-600" />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wide font-bold text-slate-400">VIN</Label>
            <div className="flex gap-2 mt-1.5">
              <Input
                data-testid="vin-input"
                className="bg-[#030712] border-slate-800 font-mono flex-1"
                value={form.vin}
                onChange={(e) => setForm({ ...form, vin: e.target.value.toUpperCase() })}
                placeholder="17 chars"
                maxLength={17}
              />
              <Button
                data-testid="decode-vin-btn"
                onClick={decodeVin}
                disabled={vinLoading || (form.vin || "").length < 11}
                variant="outline"
                className="border-blue-500/40 text-blue-300 hover:bg-blue-500/10 shrink-0"
              >
                <Hash className={`w-3.5 h-3.5 mr-1.5 ${vinLoading ? "animate-spin" : ""}`} />
                {vinLoading ? "Decoding…" : "Decode"}
              </Button>
            </div>
          </div>

          {customerVehicles.length > 0 && (
            <div className="rounded-lg border border-blue-500/20 bg-blue-500/5 p-3">
              <div className="text-[10px] uppercase tracking-wider font-bold text-blue-300 mb-2">
                Customer's vehicles · pick to autofill
              </div>
              <div className="space-y-1.5">
                {customerVehicles.map((v, i) => (
                  <button
                    key={i}
                    type="button"
                    data-testid={`customer-vehicle-${i}`}
                    onClick={() => pickVehicle(v)}
                    className={`w-full text-left rounded px-2.5 py-1.5 text-xs ${
                      form.vin && form.vin === (v.vin || "").toUpperCase()
                        ? "bg-blue-500/20 text-blue-200 border border-blue-500/40"
                        : "bg-slate-900/50 hover:bg-slate-800 text-slate-300 border border-slate-800"
                    }`}
                  >
                    <span className="font-mono">{v.vin || "no-VIN"}</span> · {v.year} {v.make} {v.model}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-xs uppercase tracking-wide font-bold text-slate-400">Year</Label>
              <Input data-testid="year-input" className="mt-1.5 bg-[#030712] border-slate-800" type="number" value={form.vehicle_year} onChange={(e) => setForm({ ...form, vehicle_year: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide font-bold text-slate-400">Make</Label>
              <Input data-testid="make-input" className="mt-1.5 bg-[#030712] border-slate-800" value={form.vehicle_make} onChange={(e) => setForm({ ...form, vehicle_make: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide font-bold text-slate-400">Model</Label>
              <Input data-testid="model-input" className="mt-1.5 bg-[#030712] border-slate-800" value={form.vehicle_model} onChange={(e) => setForm({ ...form, vehicle_model: e.target.value })} />
            </div>
          </div>
        </div>

        {/* ───── Stakeholders ───── */}
        <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-6 space-y-3.5">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Stakeholders</div>

          <EntityPicker
            entity="customers"
            label="Customer"
            testId="customer-picker"
            value={form.customer_name}
            onPick={pickCustomer}
            onClear={clearCustomer}
            placeholder="Search customer by name, email, phone or VIN…"
            required
          />

          <EntityPicker
            entity="body-shops"
            label="Body Shop"
            testId="body-shop-picker"
            value={form.body_shop_name}
            onPick={pickBodyShop}
            onClear={clearBodyShop}
            placeholder="Search body shop…"
          />

          <EntityPicker
            entity="insurance"
            label="Insurance Company"
            testId="insurance-picker"
            value={form.insurance_company}
            onPick={pickInsurance}
            onClear={clearInsurance}
            placeholder="Search insurance carrier…"
          />

          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs uppercase tracking-wide font-bold text-slate-400">Claim #</Label>
              <Input data-testid="claim-input" className="mt-1.5 bg-[#030712] border-slate-800 font-mono" value={form.claim_number} onChange={(e) => setForm({ ...form, claim_number: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide font-bold text-slate-400">Policy #</Label>
              <Input data-testid="policy-input" className="mt-1.5 bg-[#030712] border-slate-800 font-mono" value={form.policy_number} onChange={(e) => setForm({ ...form, policy_number: e.target.value })} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs uppercase tracking-wide font-bold text-slate-400">Adjuster</Label>
              <Input data-testid="adjuster-input" className="mt-1.5 bg-[#030712] border-slate-800" value={form.adjuster_name} onChange={(e) => setForm({ ...form, adjuster_name: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide font-bold text-slate-400">Adjuster Email</Label>
              <Input data-testid="adjuster-email-input" className="mt-1.5 bg-[#030712] border-slate-800 text-xs" type="email" value={form.adjuster_email} onChange={(e) => setForm({ ...form, adjuster_email: e.target.value })} />
            </div>
          </div>
        </div>

        {/* ───── Assignment ───── */}
        <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-6 space-y-3.5">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Assignment</div>
          <p className="text-xs text-slate-500">{TECHS_AND_LOCATIONS_HINT}</p>
          <EntityPicker
            entity="technicians"
            label="Technician"
            testId="tech-picker"
            value={pickedTech}
            onPick={pickTech}
            onClear={clearTech}
            placeholder="Assign technician…"
          />
          <EntityPicker
            entity="locations"
            label="Location / Bay"
            testId="location-picker"
            value={pickedLocation}
            onPick={pickLocation}
            onClear={clearLocation}
            placeholder="Pick the shop location…"
          />
        </div>

        {/* ───── Notes ───── */}
        <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-6">
          <Label className="text-xs uppercase tracking-wide font-bold text-slate-400">Notes</Label>
          <Textarea data-testid="notes-input" className="mt-1.5 bg-[#030712] border-slate-800 min-h-[120px]" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={5} />
        </div>
      </div>

      {/* ───── Estimate lines ───── */}
      <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Estimate Lines</div>
            <h3 className="font-display text-xl font-medium mt-1">Operations & Labor</h3>
          </div>
          <Button data-testid="add-line-btn" size="sm" variant="outline" onClick={addLine} className="border-slate-700"><Plus className="w-3.5 h-3.5 mr-1" />Add line</Button>
        </div>
        {lines.length === 0 ? (
          <div className="text-sm text-slate-500 text-center py-10 border border-dashed border-slate-800 rounded-lg">
            <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
            No lines yet — add an ADAS operation or run OCR to extract from PDF.
          </div>
        ) : (
          <div className="space-y-2">
            {lines.map((l, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-end">
                <Input className="col-span-2 bg-[#030712] border-slate-800" placeholder="Op" value={l.operation} onChange={(e) => updateLine(i, "operation", e.target.value)} />
                <Input className="col-span-5 bg-[#030712] border-slate-800" placeholder="Description" value={l.description} onChange={(e) => updateLine(i, "description", e.target.value)} />
                <Input className="col-span-2 bg-[#030712] border-slate-800 font-mono" placeholder="Part #" value={l.part_number || ""} onChange={(e) => updateLine(i, "part_number", e.target.value)} />
                <Input className="col-span-1 bg-[#030712] border-slate-800 font-mono" type="number" step="0.1" placeholder="Hrs" value={l.hours} onChange={(e) => updateLine(i, "hours", e.target.value)} />
                <Input className="col-span-1 bg-[#030712] border-slate-800 font-mono" type="number" step="0.01" placeholder="$" value={l.amount} onChange={(e) => updateLine(i, "amount", e.target.value)} />
                <button onClick={() => removeLine(i)} className="col-span-1 text-slate-500 hover:text-red-400 flex items-center justify-center"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            <div className="mt-3 pt-3 border-t border-slate-800 text-right text-sm text-slate-400">
              Subtotal: <span className="font-mono text-emerald-400 ml-2">${lines.reduce((s, l) => s + (l.amount || 0), 0).toFixed(2)}</span>
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-end gap-3">
        <Button variant="outline" className="border-slate-700" onClick={() => navigate("/jobs")} data-testid="cancel-btn">Cancel</Button>
        <Button data-testid="create-job-btn" onClick={submit} disabled={saving} className="bg-blue-600 hover:bg-blue-500">
          {saving ? "Creating…" : "Create Job & Analyze ADAS"}
        </Button>
      </div>
    </div>
  );
}
